/**
 * A rule, as one plain line.
 *
 * `docs/plans/argus.md` rule 13: whatever door a rule was written through, it renders as a
 * sentence somebody can read aloud in a meeting, and that sentence is the
 * acceptance test for the rule.
 *
 * This is the *preview* copy. The stored one is written by the server when the
 * version is written (`backend/argus_rules.py`), because what a rule said at
 * the time is what a post-mortem needs and a renderer that has moved on would
 * rewrite history. The two are kept in step deliberately: a difference between
 * what the form promised and what was stored is a bug worth failing a test over.
 */

import type { ArgusRuleDocument } from '../../api/types.ts';

const WINDOW_WORDS: Record<string, string> = {
  '1h': '1 hour',
  '6h': '6 hours',
  '12h': '12 hours',
  '24h': '24 hours',
  '3d': '3 days',
  '7d': '7 days',
  '30d': '30 days',
};

const AGGREGATE_WORDS: Record<string, string> = {
  mean: 'average',
  max: 'highest',
  min: 'lowest',
  p95: '95th percentile',
  sum: 'total',
  count_missing: 'missing readings',
};

function numberWord(value: number): string {
  return Number.isInteger(value) ? String(value) : String(value);
}

/**
 * The line for a document in progress.
 *
 * `entityName` is passed in rather than looked up, because the form already has
 * it and this module has no business fetching anything while somebody types.
 */
export function ruleSentence(document: ArgusRuleDocument, entityName: string): string {
  const series = document.subject?.series ?? '';
  const entity = entityName || document.subject?.entity || '';
  if (!series || !entity) return '';

  const window = WINDOW_WORDS[document.window] ?? document.window;
  const parts = [`Watch ${series} on ${entity}`];

  const gates = document.gates ?? [];
  if (gates.length) {
    parts.push(
      `when ${gates.map((g) => `${g.series} ${g.op} ${numberWord(g.value)}`).join(' and ')}`,
    );
  }

  if (document.shape === 'silence') {
    return `${parts.join(' ')} - alert when no reading arrives for ${window}.`;
  }

  const direction = document.test?.direction === 'below' ? 'below' : 'above';
  const value = numberWord(document.test?.value ?? 0);

  if (document.shape === 'single') {
    parts.push(`- alert when any single reading is ${direction} ${value}`);
  } else if (document.shape === 'sustained') {
    const dwell = WINDOW_WORDS[document.persistence?.dwell ?? '1h'] ?? '1 hour';
    parts.push(`- alert when it stays ${direction} ${value} for ${dwell}`);
  } else if (document.shape === 'n_of_m') {
    const n = document.persistence?.n ?? 3;
    const m = document.persistence?.m ?? 5;
    parts.push(`- alert when ${n} of the last ${m} ${window} windows are ${direction} ${value}`);
  } else {
    const aggregate = AGGREGATE_WORDS[document.aggregate ?? 'mean'] ?? 'average';
    parts.push(`- alert when the ${window} ${aggregate} is ${direction} ${value}`);
  }

  return `${parts.join(' ')}.`;
}

/**
 * What a shape means, in the words the picker shows beside it.
 *
 * The single reading is listed first because it is the one people reach for and
 * the one that cries wolf - so the warning sits next to the choice rather than
 * arriving after the rule has been written.
 */
export const SHAPE_WORDS: ReadonlyArray<{ id: string; label: string; hint: string }> = [
  {
    id: 'single',
    label: 'Any single reading',
    hint: 'Fires on one datapoint. Right for a hard limit, noisy for anything else.',
  },
  {
    id: 'sustained',
    label: 'Sustained',
    hint: 'Has to stay over the line for a while. Most real deviations look like this.',
  },
  { id: 'n_of_m', label: 'N of M windows', hint: 'Tolerates a noisy series without missing a trend.' },
  {
    id: 'window',
    label: 'Window aggregate',
    hint: 'The average, peak or percentile over a window. Consumption and efficiency.',
  },
  {
    id: 'silence',
    label: 'No data',
    hint: 'A reading that should have arrived and did not. Its own kind of problem.',
  },
];
